import axios, { AxiosResponse } from "axios";

import {
    AutocompleteResult,
    SearchResult,
    CategoryResult,
} from "./models/search.models";

export * from "./models/search.models";

const TOUCHPOINT_DESKTOP = "DESKTOP";
const TOUCHPOINT_MOBILE = "MOBILE";

export default class SearchService {
    /**
     * Notify add to cart
     *
     * If browser does not support beacon: do an axios request and return promise.
     * Otherwise: sendBeacon.
     */
    static notifyAddToCart(
        ticket: string,
    ): undefined | Promise<AxiosResponse<any>> {
        const url = `/publicapi/esales/notify/addtocart?ticket=${ticket}`;

        if (hasBeacon()) {
            navigator.sendBeacon(url);
        } else {
            return axios.post(url);
        }
    }
    /**
     * Notify click
     *
     * If browser does not support beacon: do an axios request and return promise.
     * Otherwise: sendBeacon.
     */
    static notifyClick(
        ticket: string,
    ): undefined | Promise<AxiosResponse<any>> {
        const url = `/publicapi/esales/notify/click?ticket=${ticket}`;

        if (hasBeacon()) {
            navigator.sendBeacon(url);
        } else {
            return axios.post(url);
        }
    }
    /**
     * Autocomplete
     */
    static autocomplete(
        abortController: AbortController,
        searchQuery: string,
    ): Promise<AxiosResponse<AutocompleteResult>> {
        return axios.get("/publicapi/esales/autocomplete", {
            signal: abortController.signal,
            params: {
                q: searchQuery,
                touchpoint: this.getTouchpoint(),
            },
        });
    }

    /**
     * Search
     */
    static search(
        abortController: AbortController,
        searchQuery: string,
        searchFacets: Object,
        sort: string | null,
        pageSize: number,
        page = 0,
        preview: string | undefined,
    ): Promise<AxiosResponse<SearchResult>> {
        const [query, skip, limit] = getSearchQuery(
            searchFacets,
            page,
            pageSize,
        );
        return axios.get("/publicapi/esales/search", {
            signal: abortController.signal,
            params: Object.assign({}, query, {
                q: searchQuery,
                touchpoint: this.getTouchpoint(),
                sort: sort || undefined,
                skip,
                limit,
                PREVIEW: preview,
            }),
        });
    }

    /**
     * Category
     */
    static category(
        abortController: AbortController,
        pageId: string,
        searchFacets: Object,
        sort: string | null,
        pageSize: number,
        page = 0,
        preview: string | undefined,
        contentLinkId: number | undefined,
    ): Promise<AxiosResponse<CategoryResult>> {
        const [query, skip, limit] = getSearchQuery(
            searchFacets,
            page,
            pageSize,
        );
        return axios.get("/publicapi/esales/landing-page", {
            signal: abortController.signal,
            params: Object.assign({}, query, {
                pageId,
                touchpoint: this.getTouchpoint(),
                sort: sort || undefined,
                skip,
                limit,
                PREVIEW: preview,
                contentLinkId: contentLinkId,
            }),
        });
    }

    private static getTouchpoint() {
        if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
            return TOUCHPOINT_MOBILE;
        }

        return TOUCHPOINT_DESKTOP;
    }
};

function getSearchQuery(searchFacets: Object, page: number, pageSize: number) {
    const query = {};

    Object.keys(searchFacets).forEach((facetId) => {
        const facetValues = searchFacets[facetId].join("|");

        if (facetValues) {
            query[`f.${facetId}`] = facetValues;
        }
    });

    const skip = 0;
    const limit = (page + 1) * pageSize;

    return [query, skip, limit];
}

function hasBeacon() {
    return !!navigator.sendBeacon;
}